import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { LogOut, Inbox, Sparkles, Briefcase, Settings as SettingsIcon, ShieldCheck, Heart } from 'lucide-react'
import { supabase } from '../api/supabase'
import { CustomerShell } from './CustomerShell'

interface PublicLayoutProps {
  children: ReactNode
}

interface SessionUser {
  id: string
  display_name: string | null
  role: string
}

export function PublicLayout({ children }: PublicLayoutProps) {
  const [me, setMe] = useState<SessionUser | null | 'loading'>('loading')

  useEffect(() => {
    let mounted = true

    // Hard timeout: if auth/account resolution takes more than 3 seconds
    // (network glitch, hung query, RLS surprise), fall back to "signed out"
    // so the Sign-in button always shows. Better to make a signed-in user
    // click sign-in than leave them with a blank header and no escape hatch.
    const timeout = setTimeout(() => {
      if (mounted) {
        setMe(prev => (prev === 'loading' ? null : prev))
      }
    }, 3000)

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!mounted) return
      if (!session?.user) {
        setMe(null)
        clearTimeout(timeout)
        return
      }
      const { data: account } = await supabase
        .from('accounts')
        .select('id, display_name, role')
        .eq('id', session.user.id)
        .maybeSingle()
      if (!mounted) return
      clearTimeout(timeout)
      if (account) {
        setMe({ id: account.id, display_name: account.display_name, role: account.role })
      } else {
        setMe(null)
      }
    })

    return () => {
      mounted = false
      clearTimeout(timeout)
    }
  }, [])

  async function handleSignOut() {
    await supabase.auth.signOut()
    window.location.href = '/'
  }

  // Signed-in customers get the Instagram-style chrome (bottom nav on
  // mobile, left sidebar on desktop). Vendors / super-admins / signed-
  // out users get the marketing top-nav layout.
  if (me !== 'loading' && me?.role === 'customer') {
    return <CustomerShell>{children}</CustomerShell>
  }

  return (
    <div className="min-h-screen bg-bone">
      {/* Top nav */}
      <nav className="sticky top-0 z-30 bg-bone/90 backdrop-blur-md border-b border-line">
        <div className="mx-auto max-w-wide px-5 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="brand-mark brand-mark--sm">p</div>
            <span className="font-serif text-xl font-semibold tracking-tight">PlanSync</span>
          </Link>
          <div className="flex items-center gap-1 sm:gap-3">
            <Link to="/discover" className="text-sm font-medium text-ink-2 hover:text-ink px-2 py-1.5">
              Discover
            </Link>
            {me === 'loading' ? null : me ? (
              <>
                <Link
                  to="/account/inbox"
                  className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-ink-2 hover:text-ink px-2 py-1.5"
                >
                  <Inbox className="w-4 h-4" />
                  Inbox
                </Link>
                <Link
                  to="/account/saved"
                  className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-ink-2 hover:text-ink px-2 py-1.5"
                >
                  <Heart className="w-4 h-4" />
                  Saved
                </Link>
                <Link
                  to="/account/plan"
                  className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-ink-2 hover:text-ink px-2 py-1.5"
                >
                  <Sparkles className="w-4 h-4" />
                  My events
                </Link>
                {me.role === 'super_admin' && (
                  <Link
                    to="/admin"
                    className="inline-flex items-center gap-1 text-sm font-medium text-orange-deep hover:bg-orange-mist px-3 py-1.5 rounded-pill transition-colors"
                    title="Admin tools"
                  >
                    <ShieldCheck className="w-4 h-4" />
                    <span className="hidden sm:inline">Admin</span>
                  </Link>
                )}
                {me.role === 'vendor_member' || me.role === 'super_admin' ? (
                  <Link
                    to="/vendor"
                    className="inline-flex items-center gap-1 text-sm font-semibold px-4 py-2 bg-ink text-cream rounded-pill hover:bg-orange transition-colors"
                  >
                    <Briefcase className="w-4 h-4" />
                    Cockpit
                  </Link>
                ) : (
                  <Link
                    to="/auth/vendor-application"
                    className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-orange-deep hover:underline px-2 py-1.5"
                  >
                    Become a vendor
                  </Link>
                )}
                <Link
                  to="/account/settings"
                  className="inline-flex items-center gap-1 text-sm text-ink-3 hover:text-ink px-2 py-1.5"
                  aria-label="Settings"
                  title="Settings"
                >
                  <SettingsIcon className="w-4 h-4" />
                </Link>
                <button
                  onClick={handleSignOut}
                  className="inline-flex items-center gap-1 text-sm text-ink-3 hover:text-ink px-2 py-1.5"
                  aria-label="Sign out"
                  title="Sign out"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            ) : (
              <Link
                to="/auth/login"
                className="text-sm font-semibold px-4 py-2 bg-ink text-cream rounded-pill hover:bg-orange transition-colors"
              >
                Sign in
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* Page content */}
      <main className="mx-auto max-w-wide">{children}</main>

      {/* Footer */}
      <footer className="mt-20 py-12 border-t border-line bg-cream">
        <div className="mx-auto max-w-wide px-5 grid grid-cols-1 md:grid-cols-3 gap-8 text-sm">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="brand-mark brand-mark--xs">p</div>
              <span className="font-serif text-base font-semibold tracking-tight">PlanSync</span>
            </div>
            <p className="text-ink-3 text-xs leading-relaxed">
              Event vendor marketplace for Maldives. Discover and book photographers, venues,
              caterers and more.
            </p>
          </div>
          <div>
            <h6 className="font-semibold mb-3 text-xs uppercase tracking-wider text-ink-3">
              For couples
            </h6>
            <ul className="space-y-2 text-ink-2">
              <li><Link to="/discover">Browse vendors</Link></li>
              <li><Link to="/account/plan">Plan your event</Link></li>
            </ul>
          </div>
          <div>
            <h6 className="font-semibold mb-3 text-xs uppercase tracking-wider text-ink-3">
              For vendors
            </h6>
            <ul className="space-y-2 text-ink-2">
              <li><Link to="/auth/vendor-application">List your business</Link></li>
              <li><Link to="/auth/login">Vendor sign in</Link></li>
            </ul>
          </div>
        </div>
        <div className="mx-auto max-w-wide px-5 mt-8 pt-6 border-t border-line text-xs text-ink-4 flex flex-col sm:flex-row gap-3 sm:gap-6 sm:justify-between">
          <span>© 2026 PlanSync. All rights reserved.</span>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-ink">Privacy</Link>
            <Link to="/terms" className="hover:text-ink">Terms</Link>
            <Link to="/status" className="hover:text-ink">Status</Link>
          </div>
          <span>Made in Maldives 🇲🇻</span>
        </div>
      </footer>
    </div>
  )
}
